// Handicap utilities — shared between the admin tournament setup, the
// public registration form, and the player list view. Pure functions
// only so callers can render previews without any side effects.
//
// Three handicap modes the director can pick at tournament setup. They
// drive what fields show on the registration form AND what columns are
// rendered in the player / registration tables.

export const HANDICAP_MODES = {
  REQUIRED: 'required',     // GHIN # required, Handicap Index required
  OPTIONAL: 'optional',     // GHIN # optional, Handicap Index required if no GHIN
  NONE:     'none',         // scratch play — no handicap fields anywhere
}

export const HANDICAP_MODE_OPTIONS = [
  { value: 'none',     label: 'No Handicap',   desc: 'Scratch play — no handicap fields shown.' },
  { value: 'optional', label: 'Handicap (GH) ID Optional', desc: 'Player enters Handicap (GH) ID if they have it; manual handicap index entry if not.' },
  { value: 'required', label: 'Handicap (GH) ID Required', desc: 'Player must enter Handicap (GH) ID to register.' },
]

// USGA GHIN allowance percentages — exact list matching the GolfGamesScorecard
// app picker and the USGA GHIN app picker. High → low so the most common
// defaults sit first. Default is always 100%.
export const ALLOWANCE_OPTIONS = [100, 95, 90, 85, 80, 75, 70, 60, 50, 40, 35, 30, 25, 20, 15, 10, 5]

export const DEFAULT_ALLOWANCE = 100

/**
 * USGA WHS course handicap.
 *   Full:       CH = Index × (Slope ÷ 113) + (Rating − Par)
 *   Simplified: CH = Index × (Slope ÷ 113)
 *
 * Uses the full formula when both `courseRating` and `par` are finite +
 * positive; otherwise falls back to the slope-only form so older
 * tournaments (saved before the course-search feature) still produce
 * a sensible number instead of NaN.
 */
export function calcCourseHandicap(handicapIndex, slopeRating, courseRating, par) {
  const idx   = Number(handicapIndex)
  const slope = Number(slopeRating)
  if (!Number.isFinite(idx) || !Number.isFinite(slope) || slope <= 0) return 0
  const rating = Number(courseRating)
  const parNum = Number(par)
  if (Number.isFinite(rating) && rating > 0 && Number.isFinite(parNum) && parNum > 0) {
    return Math.round(idx * (slope / 113) + (rating - parNum))
  }
  return Math.round(idx * (slope / 113))
}

/**
 * Playing Handicap = Course Handicap × (Allowance ÷ 100), rounded.
 * Allowance defaults to 100 (no adjustment). Used by the director's
 * tournament-wide allowance setting so every player's strokes flex
 * uniformly with the format.
 */
export function calcPlayingHandicap(courseHandicap, allowance = DEFAULT_ALLOWANCE) {
  const ch = Number(courseHandicap)
  const al = Number(allowance)
  if (!Number.isFinite(ch)) return 0
  if (!Number.isFinite(al) || al <= 0) return ch
  return Math.round(ch * (al / 100))
}

/** Strip everything but digits from a typed GHIN number. Empty string
 *  returns ''. Lets us store a clean numeric string regardless of how
 *  the player typed it ("1234-5678", "1234 5678", etc.). */
export function normalizeGhin(value) {
  if (value == null) return ''
  return String(value).replace(/\D+/g, '')
}

/** True when a GHIN number is well-formed (7–10 digits after normalize).
 *  Used to surface a soft validation hint on the registration form. */
export function isValidGhin(value) {
  const n = normalizeGhin(value)
  return n.length >= 7 && n.length <= 10
}
